import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
  initializeMathQuill,
  replaceMathHtmlWithLatexes,
} from '../../utils/mathUtils'
import { WithResources } from '../../HOC/withResources'
import AppConfig from '../../../../../src/app-config'
import { configureJqueryHtmlPrefilter } from '../../helpers'
import { getApplicableMathToolsForTestSkin } from '../../../../../src/client/assessment/themes/utils'
import {
  TextAndMathEditorContainer,
  TextAndMathEditorToolbar,
  TextAndMathEditorToolbarButton,
  TextAndMathEditorToolbarButtons,
} from './styled'
import FlexContainer from '../FlexContainer'

// Helper function to replace spaces with &nbsp; but not inside math elements
const replaceSpacesOutsideMath = (html) => {
  // Create a temporary container to work with the HTML
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html

  // Find all math elements and temporarily replace them with placeholders
  const mathElements = tempDiv.querySelectorAll('span.input__math')
  const mathPlaceholders = []

  mathElements.forEach((mathEl, index) => {
    const placeholder = `__MATH_PLACEHOLDER_${index}__`
    mathPlaceholders.push(mathEl.outerHTML)
    mathEl.outerHTML = placeholder
  })

  // Replace spaces in the remaining content
  let result = tempDiv.innerHTML.replace(/ /g, '&nbsp;')

  // Restore math elements
  mathPlaceholders.forEach((mathHTML, index) => {
    const placeholder = `__MATH_PLACEHOLDER_${index}__`
    result = result.replace(placeholder, mathHTML)
  })

  return result
}

const TextAndMathEditor = ({
  value,
  onChange,
  className,
  placeholder = '',
  readOnly = false,
  editorHeight = null,
  fontSize = '14px',
  border = 'none',
  centerContent = false,
  footer = null,
  subjects,
  grades,
  isStudentAttempt,
  isTestPreview,
  isExpressGrader,
}) => {
  const editorRef = useRef(null)

  const lastCursorPosition = useRef(null)
  // Add ref to store event listeners
  const eventListenersRef = useRef(new Map())
  // Debounce timer for input events
  const inputDebounceTimer = useRef(null)
  // Cache for processed content to avoid unnecessary processing
  const lastProcessedContent = useRef('')
  // Track previous value to implement conditional updates
  const previousValueRef = useRef(value)

  const editorStyle = {
    minHeight: editorHeight || 120,
    fontSize,
    border,
    outline: 'none',
    padding: 8,
    textAlign: centerContent ? 'center' : 'left',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    pointerEvents: readOnly ? 'none' : 'auto',
  }

  const onInsertMath = useCallback(
    (latex = '') => {
      if (!editorRef.current || readOnly) return

      // Focus on the editor first to ensure proper selection
      editorRef.current.focus()

      // Try to restore the last cursor position, or get current selection
      let range
      const selection = window.getSelection()

      // First try to restore the last saved cursor position
      if (lastCursorPosition.current) {
        try {
          selection.removeAllRanges()
          selection.addRange(lastCursorPosition.current)
          range = selection.getRangeAt(0)
        } catch (e) {
          // If restoration fails, fall back to current selection
          lastCursorPosition.current = null
        }
      }

      // If no saved position or restoration failed, use current selection
      if (!range) {
        if (
          selection.rangeCount > 0 &&
          editorRef.current.contains(selection.anchorNode)
        ) {
          range = selection.getRangeAt(0)
        } else {
          // No valid selection, create one at the end
          range = document.createRange()
          range.selectNodeContents(editorRef.current)
          range.collapse(false) // collapse to end
          selection.removeAllRanges()
          selection.addRange(range)
        }
      }

      // Create wrapper span
      const wrapper = document.createElement('span')
      wrapper.className = 'input__math'
      wrapper.setAttribute('contenteditable', 'false')
      wrapper.setAttribute('data-latex', latex)

      // Create field span
      const fieldSpan = document.createElement('span')
      fieldSpan.className = 'input__math__field'
      fieldSpan.setAttribute('contenteditable', 'false')
      wrapper.appendChild(fieldSpan)

      // Check if current selection is within a math parent
      const mathParent =
        range.commonAncestorContainer.nodeType === Node.TEXT_NODE
          ? range.commonAncestorContainer.parentElement?.closest(
              'span.input__math'
            )
          : range.commonAncestorContainer.closest('span.input__math')

      if (mathParent) {
        // If selection is within a math element, find the MathQuill instance and write LaTeX
        const mathField = mathParent.querySelector('.input__math__field')
        if (mathField) {
          // Use requestAnimationFrame to defer MathQuill operations
          requestAnimationFrame(() => {
            const MQ = window.MathQuill.getInterface(2)
            const mf = MQ.MathField(mathField)

            // Remove existing listener if any
            const existingListener = eventListenersRef.current.get(mathField)
            if (existingListener) {
              mf.el().removeEventListener('input', existingListener)
            }

            // Create new listener function with debouncing
            let updateTimeout = null
            const updateLatexAttribute = () => {
              if (updateTimeout) clearTimeout(updateTimeout)
              updateTimeout = setTimeout(() => {
                const updatedLatex = mf.latex()
                mathParent.setAttribute('data-latex', updatedLatex)
              }, 50)
            }

            // Store the listener reference and add it
            eventListenersRef.current.set(mathField, updateLatexAttribute)
            mf.el().addEventListener('input', updateLatexAttribute)

            // Use MathQuill's write method to insert LaTeX at cursor position
            mf.write(latex)

            // Update data-latex attribute after writing
            const currentLatex = mf.latex()
            mathParent.setAttribute('data-latex', currentLatex)

            mf.focus()
          })
        }
      } else {
        // Create new math element
        // Create wrapper span
        const newWrapper = document.createElement('span')
        newWrapper.className = 'input__math'
        newWrapper.setAttribute('contenteditable', 'false')
        newWrapper.setAttribute('data-latex', latex)

        // Create field span
        const newFieldSpan = document.createElement('span')
        newFieldSpan.className = 'input__math__field'
        newFieldSpan.setAttribute('contenteditable', 'false')
        newWrapper.appendChild(newFieldSpan)

        // Initialize MathQuill with the provided LaTeX using requestAnimationFrame
        requestAnimationFrame(() => {
          const mf = initializeMathQuill(newFieldSpan, latex)

          // Set up event listener to update data-latex attribute when content changes
          if (mf) {
            // Remove existing listener if any
            const existingListener = eventListenersRef.current.get(newFieldSpan)
            if (existingListener) {
              mf.el().removeEventListener('input', existingListener)
            }

            // Create debounced update function
            let updateTimeout = null
            const updateLatexAttribute = () => {
              if (updateTimeout) clearTimeout(updateTimeout)
              updateTimeout = setTimeout(() => {
                const updatedLatex = mf.latex()
                newWrapper.setAttribute('data-latex', updatedLatex)
              }, 50)
            }

            // Store the listener reference and add it
            eventListenersRef.current.set(newFieldSpan, updateLatexAttribute)
            mf.el().addEventListener('input', updateLatexAttribute)
          }
        })

        // Insert the wrapper at the current selection
        range.deleteContents()
        range.insertNode(newWrapper)

        // Add a space after the math element
        const spaceNode = document.createTextNode(' ')
        range.setStartAfter(newWrapper)
        range.setEndAfter(newWrapper)
        range.insertNode(spaceNode)

        // Move cursor after the space
        range.setStartAfter(spaceNode)
        range.setEndAfter(spaceNode)
        selection.removeAllRanges()
        selection.addRange(range)

        // Focus on the math field after a short delay to ensure DOM is ready
        setTimeout(() => {
          const mf = initializeMathQuill(newFieldSpan, latex)
          if (mf) {
            mf.focus()
          }
        }, 0)
      }

      // Trigger onChange to notify parent component with debouncing
      if (onChange) {
        // Clear existing timer
        if (inputDebounceTimer.current) {
          clearTimeout(inputDebounceTimer.current)
        }

        inputDebounceTimer.current = setTimeout(() => {
          const newValue = editorRef.current.innerHTML
          // Replace spaces with &nbsp; to preserve them, but not inside math elements
          const contentWithNbsp = replaceSpacesOutsideMath(newValue)
          const cleanedValue = replaceMathHtmlWithLatexes(contentWithNbsp)
          lastProcessedContent.current = newValue
          onChange(cleanedValue)
        }, 100)
      }
    },
    [readOnly, onChange]
  )

  const onInput = useCallback(() => {
    if (!onChange || !editorRef.current) return

    // Clear existing debounce timer
    if (inputDebounceTimer.current) {
      clearTimeout(inputDebounceTimer.current)
    }

    // Debounce input processing to avoid excessive calls
    inputDebounceTimer.current = setTimeout(() => {
      const currentContent = editorRef.current.innerHTML

      // Only process if content has actually changed
      if (currentContent !== lastProcessedContent.current) {
        // Replace spaces with &nbsp; to preserve them, but not inside math elements
        const contentWithNbsp = replaceSpacesOutsideMath(currentContent)
        const cleanedValue = replaceMathHtmlWithLatexes(contentWithNbsp)
        lastProcessedContent.current = currentContent
        onChange(cleanedValue)
      }
    }, 100) // 100ms debounce
  }, [onChange])

  const saveCursorPosition = useCallback(() => {
    if (!editorRef.current) return

    const selection = window.getSelection()
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0)
      if (editorRef.current.contains(range.commonAncestorContainer)) {
        lastCursorPosition.current = range.cloneRange()
      }
    }
  }, [])

  const onKeyDown = useCallback((e) => {
    // Handle special key combinations if needed
    if (e.key === 'Enter' && e.shiftKey) {
      // Allow shift+enter for new lines
    }
    // Add other key handling as needed
  }, [])

  // Memoize math buttons to avoid recalculation on every render
  const mathButtons = useMemo(() => {
    if (readOnly) {
      return []
    }
    return getApplicableMathToolsForTestSkin({
      subjects,
      grades,
      isTestAttempt: isStudentAttempt || isTestPreview || isExpressGrader,
    })
  }, [subjects, grades])

  const updateInitialValue = useCallback(() => {
    if (editorRef.current && (value || value === '')) {
      // Convert &nbsp; back to regular spaces for display in the editor
      const displayValue = value.replace(/&nbsp;/g, ' ')
      editorRef.current.innerHTML = displayValue

      // Initialize MathQuill for any existing math elements
      const mathParents = editorRef.current.querySelectorAll(
        '.input__math[data-latex]'
      )
      mathParents.forEach((wrapper) => {
        // Create .input__math__field if it doesn't exist
        let mathField = wrapper.querySelector('.input__math__field')
        if (!mathField) {
          mathField = document.createElement('span')
          mathField.className = 'input__math__field'
          mathField.setAttribute('contenteditable', 'false')
          wrapper.appendChild(mathField)
        }

        // Initialize MathQuill if not already initialized
        if (!mathField.hasAttribute('data-mathquill-initialized')) {
          const latex = wrapper.getAttribute('data-latex') || ''
          const mf = initializeMathQuill(mathField, latex)

          if (mf) {
            // Mark as initialized
            mathField.setAttribute('data-mathquill-initialized', 'true')

            // Remove existing listener if any
            const existingListener = eventListenersRef.current.get(mathField)
            if (existingListener) {
              mf.el().removeEventListener('input', existingListener)
            }

            // Set up event listener to update data-latex attribute when content changes
            const updateLatexAttribute = () => {
              const updatedLatex = mf.latex()
              wrapper.setAttribute('data-latex', updatedLatex)
            }

            // Store the listener reference and add it
            eventListenersRef.current.set(mathField, updateLatexAttribute)
            mf.el().addEventListener('input', updateLatexAttribute)
          }
        }
      })
    }
  }, [value])

  const initializeRef = useCallback((node) => {
    if (node !== null) {
      editorRef.current = node
      updateInitialValue()
    }
  }, [])

  useEffect(() => {
    // express grader could be editable but values takes time to initialize
    if (editorRef.current && value && (isExpressGrader || readOnly)) {
      // Only update if previous value was empty string and new value is not empty string
      const previousValue = previousValueRef.current
      if ((previousValue === '' && value !== '') || readOnly) {
        updateInitialValue()
      }
    }
    // Update the previous value reference
    previousValueRef.current = value
  }, [value, isExpressGrader, readOnly])

  useEffect(() => {
    if (editorRef.current && value === '') {
      editorRef.current.innerHTML = ''
    }
  }, [value])

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Clean up debounce timer
      if (inputDebounceTimer.current) {
        clearTimeout(inputDebounceTimer.current)
      }

      // Clean up all event listeners on unmount
      eventListenersRef.current.forEach((listener, element) => {
        if (element && element.removeEventListener) {
          element.removeEventListener('input', listener)
        }
      })
      eventListenersRef.current.clear()
    }
  }, [])

  return (
    <WithResources
      criticalResources={[AppConfig.jqueryPath]}
      resources={[
        `${AppConfig.mathquillPath}/mathquill.css`,
        `${AppConfig.mathquillPath}/mathquill.min.js`,
      ]}
      fallBack={<span />}
      onLoaded={() => configureJqueryHtmlPrefilter()}
    >
      <TextAndMathEditorContainer className={className}>
        {!readOnly && (
          <TextAndMathEditorToolbar>
            <FlexContainer alignItems="center" gap="6px">
              <TextAndMathEditorToolbarButtons>
                {mathButtons.map((button) => {
                  return (
                    <TextAndMathEditorToolbarButton
                      key={button.dataCy}
                      type="button"
                      onClick={() =>
                        onInsertMath(button.mathLatex || button.handler)
                      }
                    >
                      {button.inlineMathEssayLabel}
                    </TextAndMathEditorToolbarButton>
                  )
                })}
              </TextAndMathEditorToolbarButtons>
            </FlexContainer>
          </TextAndMathEditorToolbar>
        )}
        <div
          ref={initializeRef}
          contentEditable={!readOnly}
          onInput={onInput}
          onKeyDown={onKeyDown}
          onMouseUp={saveCursorPosition}
          onKeyUp={saveCursorPosition}
          data-placeholder={placeholder}
          style={editorStyle}
          suppressContentEditableWarning
        />
        {footer ? <div style={{ marginTop: 8 }}>{footer}</div> : null}
      </TextAndMathEditorContainer>
    </WithResources>
  )
}

TextAndMathEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  editorHeight: PropTypes.number,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  border: PropTypes.string,
  centerContent: PropTypes.bool,
  footer: PropTypes.node,
}

TextAndMathEditor.defaultProps = {
  className: '',
  placeholder: '',
  readOnly: false,
  editorHeight: null,
  fontSize: '14px',
  border: 'none',
  centerContent: false,
  footer: null,
}

export default TextAndMathEditor
